import { Components } from '@/api/generated/api';
import { ILandingPreferenceContext } from '@/interfaces';

export type TClassSettingsPage = 'schoolSubjects' | 'students';

/** Display assignments for one school year, including its archived classes and groups. */
export const getSchoolClassesForYear = <T extends { schoolYearId: number | null }>(
  schoolClasses: readonly T[],
  schoolYearId: number | null | undefined,
): T[] =>
  schoolYearId == null ? [] : schoolClasses.filter((schoolClass) => schoolClass.schoolYearId === schoolYearId);

export const getClassNamePrefix = (
  schoolLevel: number,
  schoolLevels: number[],
  useRawSchoolLevelLabels: boolean,
): string => {
  if (schoolLevel === 0) {
    return 'V';
  }
  const index = schoolLevels.indexOf(schoolLevel);
  const label = useRawSchoolLevelLabels || schoolLevels.includes(0) || index < 0 ? schoolLevel : index + 1;
  return String(label);
};

export const getPreferredClassOrGroup = (
  classesOrGroups: Components.Schemas.SchoolClassDto[] | undefined,
): Components.Schemas.SchoolClassDto | undefined => {
  const selectableClassesOrGroups = classesOrGroups?.filter((schoolClass) => !schoolClass.archivedAt);
  if (!selectableClassesOrGroups?.length) return undefined;
  return selectableClassesOrGroups.find((schoolClass) => !schoolClass.isDemoClass) ?? selectableClassesOrGroups[0];
};

export const getTenantScopedClassOrGroup = (
  schoolClass: Components.Schemas.SchoolClassDto | null | undefined,
  currentTenantId: number | null | undefined,
): Components.Schemas.SchoolClassDto | null => {
  if (!schoolClass) return null;
  if (!currentTenantId) return null;
  if (schoolClass.tenantId != null && schoolClass.tenantId !== currentTenantId) return null;
  return schoolClass;
};

export const getPreferredClassOrGroupFromLandingPreference = (
  classesOrGroups: Components.Schemas.SchoolClassDto[] | undefined,
  landingPreference: ILandingPreferenceContext | null | undefined,
): Components.Schemas.SchoolClassDto | undefined => {
  if (!classesOrGroups?.length) return undefined;
  if (landingPreference?.context !== 'class' || landingPreference.classId == null) return undefined;

  return classesOrGroups.find((schoolClass) => schoolClass.id === landingPreference.classId && !schoolClass.archivedAt);
};

export const getClassSettingsPath = (schoolClassId: number, page: TClassSettingsPage): string => {
  return `/class/${schoolClassId}/settings/${page}`;
};

export const buildClassSettingsUrl = (
  schoolClassId: number,
  page: TClassSettingsPage,
  origin = globalThis.location.origin,
): string => {
  return `${origin}${getClassSettingsPath(schoolClassId, page)}`;
};
